import {
  SiteData,
  SitePage,
  PublishedSiteSnapshot,
  SiteVersion,
  SiteSEO,
  PublishReport,
  PublishStage,
} from '../types';
import { realtimeDbService as dbService } from './realtimeDbService';
import { escapeHtml, sanitizeUrl, normalizeDomain } from '../utils/security';
import { StandardCdnProvider } from './providerInterfaces';
import { SubscriptionService } from './subscriptionService';

export class PublishPipelineService {
  private static cdnProvider = new StandardCdnProvider();

  /**
   * Executes the 10-stage atomic publish pipeline
   */
  public static async executePublish(siteId: string, publishedBy = 'system'): Promise<PublishReport> {
    const completedStages: PublishStage[] = [];
    let currentStage: PublishStage = 'DRAFT';

    try {
      // 0. Permission & Subscription Check
      const canPub = await SubscriptionService.canPublish(siteId, publishedBy);
      if (!canPub.allowed) {
        throw new Error(canPub.reason || 'Yayınlama yetkiniz bulunmamaktadır.');
      }

      // STAGE 1: DRAFT Inspection
      currentStage = 'DRAFT';
      const site = await dbService.getSiteById(siteId);
      if (!site) throw new Error('Yayınlanacak site bulunamadı.');
      completedStages.push('DRAFT');

      // STAGE 2: VALIDATE Structure
      currentStage = 'VALIDATE';
      if (!site.pages || site.pages.length === 0) {
        throw new Error('Sitede hiç sayfa bulunmuyor. Lütfen en az bir sayfa ekleyin.');
      }
      completedStages.push('VALIDATE');

      // STAGE 3: SANITIZE Content
      currentStage = 'SANITIZE';
      const homePage = site.pages.find((p) => p.isHomePage && !p.isDeleted);
      if (!homePage && site.pages.length > 0) {
        site.pages[0].isHomePage = true;
      }

      let totalSections = 0;
      const sanitizedPages: SitePage[] = (site.pages || [])
        .filter((p) => p && !p.isDeleted && p.isPublished !== false)
        .map((page) => {
          const validSections = (page?.sections || [])
            .filter((s) => !s.isDeleted && s.isVisible !== false)
            .map((s) => ({
              ...s,
              content: {
                ...s.content,
                title: escapeHtml(s.content?.title),
                subtitle: escapeHtml(s.content?.subtitle),
                buttonLink: sanitizeUrl(s.content?.buttonLink),
                secondaryButtonLink: sanitizeUrl(s.content?.secondaryButtonLink),
              },
            }));
          totalSections += validSections.length;
          return {
            ...page,
            sections: validSections,
          };
        });

      if (totalSections === 0) {
        throw new Error('Sitede yayınlanabilir aktif bir bölüm (section) bulunamadı.');
      }
      completedStages.push('SANITIZE');

      // STAGE 4: BUILD SNAPSHOT
      currentStage = 'BUILD_SNAPSHOT';
      const navigation = sanitizedPages.map((p, idx) => ({
        id: p.id,
        title: p.title,
        slug: p.slug,
        isHomePage: Boolean(p.isHomePage),
        order: p.order ?? idx,
      }));

      // STAGE 5: GENERATE SEO
      currentStage = 'GENERATE_SEO';
      const canonicalDomain = site.customDomain
        ? `https://${normalizeDomain(site.customDomain)}`
        : `https://www.adiosoft.com/${site.slug || site.subdomain || 'site'}`;

      const seo: SiteSEO = {
        title: site.settings?.seo?.title || `${site.name} — Resmi Web Sitesi`,
        description: site.settings?.seo?.description || `${site.name} kurumsal web platformu.`,
        keywords: site.settings?.seo?.keywords || [
          (site.name || '').toLowerCase(),
          (site.industry || '').toLowerCase(),
        ].filter(Boolean),
        ogImage: site.settings?.seo?.ogImage || site.settings?.logoUrl || '',
        canonicalUrl: canonicalDomain,
      };
      completedStages.push('BUILD_SNAPSHOT');
      completedStages.push('GENERATE_SEO');

      // STAGE 6: GENERATE ROUTES
      currentStage = 'GENERATE_ROUTES';
      const routeManifest = sanitizedPages.map((p) => p.slug);
      completedStages.push('GENERATE_ROUTES');

      // STAGE 7: CREATE VERSION
      currentStage = 'CREATE_VERSION';
      const now = Date.now();
      const nextVersionNum = (site.publishedVersion || 0) + 1;
      const versionId = `v${nextVersionNum}_${now.toString(36)}`;

      const snapshot: PublishedSiteSnapshot = {
        versionId,
        siteId,
        publishedAt: now,
        publishedBy,
        name: site.name,
        industry: site.industry,
        templateId: site.templateId,
        templateVersion: 1,
        theme: {
          primaryColor: site.settings?.primaryColor || '#0284C7',
          secondaryColor: site.settings?.secondaryColor || '#0F172A',
          accentColor: site.settings?.accentColor || '#38BDF8',
          fontFamily: site.settings?.fontFamily || 'Inter, sans-serif',
          headingFont: site.settings?.headingFont || 'Plus Jakarta Sans, sans-serif',
          logoUrl: site.settings?.logoUrl || '',
          logoText: site.settings?.logoText || site.name,
        },
        settings: site.settings || {},
        navigation,
        pages: sanitizedPages,
        seo,
        status: 'PUBLISHED',
      };

      const versionRecord: SiteVersion = {
        versionId,
        siteId,
        publishedAt: now,
        publishedBy,
        summary: `Versiyon ${nextVersionNum} yayını (${sanitizedPages.length} sayfa, ${totalSections} bölüm)`,
        pageCount: sanitizedPages.length,
        sectionCount: totalSections,
        snapshot,
      };

      await dbService.saveSiteVersion(siteId, versionId, versionRecord);
      completedStages.push('CREATE_VERSION');

      // STAGE 8: UPDATE PUBLISHED POINTER ATOMICALLY
      currentStage = 'UPDATE_PUBLISHED_POINTER';
      await dbService.savePublishedSnapshot(siteId, snapshot);
      
      site.status = 'PUBLISHED';
      site.publishedVersion = nextVersionNum;
      site.publishedAt = now;
      site.publishedPages = sanitizedPages;
      await dbService.updateSite(site);
      completedStages.push('UPDATE_PUBLISHED_POINTER');

      // STAGE 9: CACHE INVALIDATION
      currentStage = 'CACHE_INVALIDATION';
      const domainsToPurge = [
        `${site.subdomain}.adiosoft.site`,
        ...(site.customDomain ? [site.customDomain] : []),
      ];
      await this.cdnProvider.purgeSiteCache(siteId, domainsToPurge);
      completedStages.push('CACHE_INVALIDATION');

      // STAGE 10: LIVE & AUDIT
      currentStage = 'LIVE';
      await dbService.createAuditLog(
        siteId,
        publishedBy,
        'SITE_PUBLISHED',
        `Site yayına alındı (Versiyon: ${nextVersionNum})`
      );
      completedStages.push('LIVE');

      return {
        success: true,
        siteId,
        versionId,
        completedStages,
        currentStage: 'LIVE',
        snapshot,
        publishedAt: now,
        cacheKeysInvalidated: domainsToPurge,
      };
    } catch (err: any) {
      console.warn(`[Publish Pipeline Stopped at stage: ${currentStage}]:`, err.message || err);
      return {
        success: false,
        siteId,
        completedStages,
        currentStage,
        error: err.message || 'Yayınlama sırasında beklenmeyen bir hata oluştu.',
      };
    }
  }

  /**
   * Rollback site to a previous published version snapshot atomically
   */
  public static async rollbackToVersion(
    siteId: string,
    versionId: string,
    userId = 'system'
  ): Promise<PublishedSiteSnapshot> {
    const versionRecord = await dbService.getSiteVersion(siteId, versionId);
    if (!versionRecord || !versionRecord.snapshot) {
      throw new Error('Hedeflenen versiyon snapshot verisi bulunamadı.');
    }

    const snapshot = versionRecord.snapshot;
    await dbService.savePublishedSnapshot(siteId, snapshot);

    const site = await dbService.getSiteById(siteId);
    if (site) {
      site.publishedPages = snapshot.pages;
      site.publishedAt = Date.now();
      await dbService.updateSite(site);
    }

    await dbService.createAuditLog(
      siteId,
      userId,
      'VERSION_ROLLBACK',
      `Site ${versionId} versiyonuna başarıyla geri döndürüldü.`
    );

    return snapshot;
  }
}
